/**
 * Google Analytics 4 Integration
 * 
 * Configuração minimalista do GA4 para rastreamento de página e eventos.
 */

// GA4 Measurement ID - substituir pelo ID real quando disponível
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || '';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Inicializa o dataLayer
export function initGA(): void {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // Controlamos manualmente
    cookie_flags: 'SameSite=None;Secure',
    custom_map: {
      custom_parameter_1: 'city_code',
      custom_parameter_2: 'property_id',
    },
  });
  
  // Carrega o script do GA4
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

// Envia pageview
export function pageview(path: string, title?: string): void {
  if (!window.gtag) return;
  
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
    city_code: 'URB',
  });
}

// Envia evento customizado
export function event(
  action: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!window.gtag) return;
  
  window.gtag('event', action, {
    ...params,
    city_code: 'URB',
  });
}

// Eventos específicos do Reserve
export const trackGA = {
  // Page views
  homeView: () => pageview('/', 'Home - Reserve Connect Urubici'),
  resultsView: (filters?: string) => pageview('/hospedagens', `Hospedagens ${filters || ''}`),
  propertyView: (slug: string, name: string) => pageview(`/hospedagem/${slug}`, `${name} - Reserve Connect`),
  
  // CTAs
  whatsappClick: (propertyName: string) => 
    event('cta_click', { type: 'whatsapp', property: propertyName }),
  phoneClick: (propertyName: string) => 
    event('cta_click', { type: 'phone', property: propertyName }),
  websiteClick: (propertyName: string) => 
    event('cta_click', { type: 'website', property: propertyName }),
  mapClick: (propertyName: string) => 
    event('cta_click', { type: 'map', property: propertyName }),
  
  // Search
  search: (query: string, resultsCount: number) => 
    event('search', { search_term: query, results_count: resultsCount }),
  
  // Filters
  filterApply: (filterType: string, filterValue: string) => 
    event('filter_apply', { filter_type: filterType, filter_value: filterValue }),
  
  // Central de Reservas
  centralSupportClick: (origin: string) => 
    event('central_support_click', { origin }),
};
